import { Analytics } from '@cobliteam/analytics';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Typography from '@cobliteam/ui/components/Typography';
import Select, { SelectContent, SelectItem, SelectTrigger, SelectValue } from '@cobliteam/ui/form/Select';
import Icon from '@cobliteam/ui/icons';
import type { Table } from '@tanstack/react-table';

interface DataTablePaginationProps<TData> {
  table: Table<TData>;
  isFetching: boolean;
}

const DataTablePagination = <TData,>({ table, isFetching }: DataTablePaginationProps<TData>) => {
  const { t } = useTranslation('ui-business');
  const totalSelected = table.getFilteredSelectedRowModel().rows.length;
  const totalRows = table.getFilteredRowModel().rows.length;
  const currentPage = totalRows ? table.getState().pagination.pageIndex + 1 : 0;
  const pageCount = table.getPageCount();

  return (
    <div className='flex items-center justify-between py-4'>
      <div className='flex-1 text-gray text-sm'>{totalSelected ? t('data_table.selected_items', { totalSelected, totalRows }) : null}</div>

      <div className='flex items-center space-x-2'>
        <div className='mr-4 flex items-center space-x-2'>
          <Typography color='secondary'>{t('data_table.items_per_page')}</Typography>
          <Select
            value={`${table.getState().pagination.pageSize}`}
            onValueChange={(value) => {
              Analytics.track('DataTable Rows Per Page Changed', {
                pageSize: value,
              });
              table.setPageSize(Number(value));
            }}
          >
            <SelectTrigger className='w-auto' size='sm'>
              <SelectValue placeholder={table.getState().pagination.pageSize} />
            </SelectTrigger>
            <SelectContent side='top'>
              {[10, 50, 100, 200].map((pageSize) => (
                <SelectItem key={pageSize} value={`${pageSize}`}>
                  {pageSize}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className='flex w-fit items-center justify-end gap-2.5'>
          {isFetching ? <Icon name='cb-loading-spin' size='sm' data-testid='loading' /> : null}
          <Typography color='secondary'>{t('data_table.page_info', { currentPage, pageCount })}</Typography>
        </div>
        <div className='flex items-center space-x-2'>
          <Button
            variant='outlined'
            color='default'
            size='sm'
            icon={<Icon name='chevron-first' size='sm' />}
            onClick={() => {
              Analytics.track('DataTable First Page Button Clicked');
              table.setPageIndex(0);
            }}
            disabled={!table.getCanPreviousPage()}
          />
          <Button
            variant='outlined'
            color='default'
            size='sm'
            icon={<Icon name='chevron-left' size='sm' />}
            onClick={() => {
              Analytics.track('DataTable Previous Page Button Clicked');
              table.previousPage();
            }}
            disabled={!table.getCanPreviousPage()}
          />
          <Button
            variant='outlined'
            color='default'
            size='sm'
            icon={<Icon name='chevron-right' size='sm' />}
            onClick={() => {
              Analytics.track('DataTable Next Page Button Clicked');
              table.nextPage();
            }}
            disabled={!table.getCanNextPage()}
          />

          <Button
            variant='outlined'
            size='sm'
            color='default'
            icon={<Icon name='chevron-last' size='sm' />}
            onClick={() => {
              Analytics.track('DataTable Last Page Button Clicked');
              table.setPageIndex(table.getPageCount() - 1);
            }}
            disabled={!table.getCanNextPage()}
          />
        </div>
      </div>
    </div>
  );
};

export default DataTablePagination;
